import { useQuery } from '@tanstack/react-query'
import { 
  KPIData, 
  StorePerformance, 
  ProductPerformance, 
  InventoryData, 
  Channel,
  DataQuality,
  GrossMarginResponse,
  AverageSellingPriceResponse,
  StorePerformanceDeepDiveResponse,
  InventoryManagementResponse,
  PriceRealizationResponse,
  InventoryCoverageResponse,
  StoreOutOfStockResponse,
  ChannelMixResponse,
  RevenueConcentrationResponse,
  RevenueUnitsDeepDiveResponse,
  ProductAnalysisDeepDiveResponse
} from '../../../shared/schema'
import { apiClient } from './api-client'
import { useTenant } from '../contexts/TenantContext'

// Dashboard KPIs
export function useKPIs(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['kpis', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      
      if (fiscalWeek) {
        params.append('fiscal_week', fiscalWeek.toString())
      }
      
      // Include retailer_id if selected (enables filtering)
      if (selectedRetailerId) {
        params.append('retailer_id', selectedRetailerId)
      }
      
      const queryString = params.toString()
      console.log(`[useKPIs] Calling API: /dashboard/kpis?${queryString}`)
      return apiClient.get<KPIData>(`/dashboard/kpis${queryString ? `?${queryString}` : ''}`)
    },
    enabled: !!fiscalWeek, // Only run query when we have a valid fiscal week
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  })
}

// Helper types for KPI detail endpoints
interface KPIRevenueDetailItem {
  name: string
  sku: string
  totalRevenue: number
  storeRevenue: number
  onlineRevenue: number
  units: number
  avgPrice: number
  retailPrice: number
  cost: number
  marginPercent: number
  marginDollars: number
}

interface KPIUnitsDetailItem {
  name: string
  sku: string
  totalUnits: number
  storeUnits: number
  onlineUnits: number
  totalUnitsLY: number
  unitsChange: number | null
  revenue: number
  avgPrice: number
  retailPrice: number
}

interface KPISKUsDetailItem {
  name: string
  sku: string
  brand_sku?: string
  revenue: number
  units: number
  avgPrice: number
  retailPrice?: number
  cost?: number
  marginPercent?: number
  marginDollars?: number
  storeRevenue?: number
  onlineRevenue?: number
  revenueLY?: number
  unitsLY?: number
  revenueChange?: number | null
  unitsChange?: number | null
  rank?: string
  featureCode?: string
  seasonCode?: string
  priceRealization?: number | null
}

interface KPIItemsResponse<T> {
  week: string
  items: T[]
}

// Store Performance
export function useStorePerformance(fiscalWeek?: number, channel?: Channel) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['store-performance', fiscalWeek, channel, selectedRetailerId],
    queryFn: () => {
      if (!fiscalWeek) {
        throw new Error('fiscalWeek is required for store performance')
      }
      const params = new URLSearchParams()
      params.append('fiscal_week', fiscalWeek.toString())
      if (channel && channel !== 'All') params.append('channel', channel)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<StorePerformance[]>(`/stores/performance${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Product Performance
export function useProductPerformance(fiscalWeek?: number, channel?: Channel, search?: string) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['product-performance', fiscalWeek, channel, search, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (channel && channel !== 'All') params.append('channel', channel)
      if (search) params.append('search', search)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<ProductPerformance[]>(`/products/performance${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Inventory Metrics
export function useInventoryMetrics(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['inventory-metrics', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscalWeek', fiscalWeek.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      return apiClient.get<InventoryData[]>(`/inventory/metrics?${params.toString()}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export interface TrendDataPoint {
  week: number
  fiscal_week_code: string | null
  week_start: string | null
  week_end: string | null
  revenue_ty: number
  revenue_ly: number
}

interface TrendOptions {
  window?: number
  weeks?: number[]
  channel?: Channel
}

// Trend Data
export function useTrendData(options: TrendOptions = {}) {
  const { window: windowSize = 8, weeks, channel } = options
  const { selectedRetailerId } = useTenant()

  return useQuery({
    queryKey: ['trend-data', windowSize, weeks, channel, selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams()

      if (weeks && weeks.length > 0) {
        params.append('weeks', JSON.stringify(weeks))
      } else {
        params.append('window', windowSize.toString())
      }

      if (channel && channel !== 'All') params.append('channel', channel)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)

      const queryString = params.toString()
      return apiClient.get<TrendDataPoint[]>(`/analytics/trends${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Fiscal Weeks
export function useFiscalWeeks() {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['fiscal-weeks', selectedRetailerId],
    queryFn: () => {
      const params = selectedRetailerId 
        ? `?retailer_id=${selectedRetailerId}` 
        : '';
      return apiClient.get<Array<{value: number, label: string, display: string}>>(`/data/fiscal-weeks${params}`)
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Calendar Weeks — NRF 4-5-4 fiscal calendar
export interface CalendarWeek {
  week_number: number
  start_date: string
  end_date: string
  display: string
  fiscal_week: number
  fiscal_year: string
  quarter: string
  period: number
  nrf_month: string
}

export interface CalendarWeeksResponse {
  weeks: CalendarWeek[]
  currentWeek: number
  currentFiscalYear: string
}

export function useCalendarWeeks() {
  return useQuery({
    queryKey: ['calendar-weeks'],
    queryFn: () => {
      return apiClient.get<CalendarWeeksResponse>(`/data/calendar-weeks`)
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Data Quality
export function useDataQuality() {
  return useQuery({
    queryKey: ['data-quality'],
    queryFn: () => apiClient.get<DataQuality>('/data/quality'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Upload functions
export async function uploadSalesData(data: any[], filename: string) {
  return apiClient.post('/upload/sales', { data, filename })
}

export async function uploadSummaryData(data: any[], filename: string) {
  return apiClient.post('/upload/summary', { data, filename })
}


// Online SKU Performance
export function useOnlineTopSKUs(fiscalWeek?: number, metric: 'revenue' | 'units' = 'revenue', limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['online-top-skus', fiscalWeek, metric, limit, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('metric', metric)
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      return apiClient.get<Array<{sku: string, revenue: number, units: number}>>(`/online/top-skus?${params.toString()}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Store SKU Performance
export function useStoreTopSKUs(fiscalWeek?: number, metric: 'revenue' | 'units' = 'revenue', limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['store-top-skus', fiscalWeek, metric, limit, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('metric', metric)
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      return apiClient.get<Array<{sku: string, revenue: number, units: number}>>(`/stores/top-skus?${params.toString()}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Top Store Locations
export function useTopStoreLocations(fiscalWeek?: number, limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['top-store-locations', fiscalWeek, limit, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      return apiClient.get<Array<{storeName: string, revenue: number, units: number}>>(`/stores/top-locations?${params.toString()}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// KPI Detail Functions for Modal Popups and Deep Dive Pages
export function useKPIRevenueDetail(fiscalWeek?: number, limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery<KPIRevenueDetailItem[]>({
    queryKey: ['kpi-revenue-detail', fiscalWeek, limit, selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const response = await apiClient.get<KPIItemsResponse<KPIRevenueDetailItem>>(`/kpi/revenue-detail?${params.toString()}`)
      return response.items ?? []
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useKPIUnitsDetail(fiscalWeek?: number, limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery<KPIUnitsDetailItem[]>({
    queryKey: ['kpi-units-detail', fiscalWeek, limit, selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const response = await apiClient.get<KPIItemsResponse<KPIUnitsDetailItem>>(`/kpi/units-detail?${params.toString()}`)
      return response.items ?? []
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useKPISKUsDetail(fiscalWeek?: number, limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery<KPISKUsDetailItem[]>({
    queryKey: ['kpi-skus-detail', fiscalWeek, limit, selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const response = await apiClient.get<KPIItemsResponse<KPISKUsDetailItem>>(`/kpi/skus-detail?${params.toString()}`)
      return response.items ?? []
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useKPIStoresDetail(fiscalWeek?: number, limit: number = 5) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['kpi-stores-detail', fiscalWeek, limit, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      params.append('limit', limit.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      return apiClient.get<Array<{name: string, revenue: number, units: number, avgPrice: number}>>(`/kpi/stores-detail?${params.toString()}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Comprehensive Product Analysis
export function useProductsComprehensive(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['products-comprehensive', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const searchParams = new URLSearchParams()
      if (fiscalWeek) {
        searchParams.append('fiscal_week', fiscalWeek.toString())
      }
      if (selectedRetailerId) {
        searchParams.append('retailer_id', selectedRetailerId)
      }
      const params = searchParams.toString() ? `?${searchParams.toString()}` : ''
      
      return apiClient.get<Array<{
        name: string,
        totalRevenue: number,
        totalUnits: number,
        storeRevenue: number,
        storeUnits: number,
        onlineRevenue: number,
        onlineUnits: number,
        eohTotal: number,
        eohStore: number,
        eohDC: number,
        wos: number,
        outOfStock: number,
        avgPrice: number,
        performanceLevel: string
      }>>(`/products/comprehensive${params}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Gross Margin Analysis
export function useGrossMargin(fiscalWeek?: number, channel: string = 'all') {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['gross-margin', fiscalWeek, channel, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (channel && channel !== 'all') params.append('channel', channel)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<GrossMarginResponse>(`/kpi/gross-margin${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Average Selling Price Analysis
export function useAverageSellingPrice(fiscalWeek?: number, channel: string = 'all') {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['average-selling-price', fiscalWeek, channel, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (channel && channel !== 'all') params.append('channel', channel)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<AverageSellingPriceResponse>(`/kpi/average-selling-price${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Price Realization Analysis
export function usePriceRealization(fiscalWeek?: number, channel: string = 'all') {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['price-realization', fiscalWeek, channel, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (channel && channel !== 'all') params.append('channel', channel)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<PriceRealizationResponse>(`/kpi/price-realization${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Inventory Coverage (WOS) Analysis
export function useInventoryCoverage(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['inventory-coverage', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<InventoryCoverageResponse>(`/kpi/inventory-coverage${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Store Out-of-Stock Analysis
export function useStoreOutOfStock(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['store-out-of-stock', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<StoreOutOfStockResponse>(`/kpi/store-out-of-stock${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Channel Mix Analysis
export function useChannelMix(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['channel-mix', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<ChannelMixResponse>(`/kpi/channel-mix${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Revenue Concentration Analysis
export function useRevenueConcentration(fiscalWeek?: number, topN: number = 3) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['revenue-concentration', fiscalWeek, topN, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (topN !== 3) params.append('top_n', topN.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<RevenueConcentrationResponse>(`/kpi/revenue-concentration${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Revenue & Units Deep Dive
export function useRevenueUnitsDeepDive(
  fiscalWeek?: number,
  view?: 'revenue' | 'units',
  searchQuery?: string,
  sortField?: string,
  sortDir?: 'asc' | 'desc',
  page?: number,
  pageSize?: number,
  historyWeeks?: number
) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['revenue-units-deep-dive', fiscalWeek, view, searchQuery, sortField, sortDir, page, pageSize, historyWeeks, selectedRetailerId],
    queryFn: () => {
      if (!fiscalWeek) {
        throw new Error('fiscal_week is required for revenue-units deep dive')
      }
      const params = new URLSearchParams()
      params.append('week', fiscalWeek.toString())
      if (view) params.append('view', view)
      if (searchQuery) params.append('q', searchQuery)
      if (sortField) params.append('sort', sortField)
      if (sortDir) params.append('dir', sortDir)
      if (page) params.append('page', page.toString())
      if (pageSize) params.append('pageSize', pageSize.toString())
      if (historyWeeks) params.append('historyWeeks', historyWeeks.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<RevenueUnitsDeepDiveResponse>(`/deep-dive/revenue-units${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Product Analysis Deep-Dive
export function useProductAnalysisDeepDive(
  fiscalWeek?: number, 
  searchQuery?: string,
  sortField?: string,
  sortDir?: 'asc' | 'desc',
  page?: number,
  pageSize?: number
) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['product-analysis-deep-dive', fiscalWeek, searchQuery, sortField, sortDir, page, pageSize, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) {
        params.append('fiscal_week', fiscalWeek.toString())
      }
      if (searchQuery) params.append('q', searchQuery)
      if (sortField) params.append('sort', sortField)
      if (sortDir) params.append('dir', sortDir)
      if (page) params.append('page', page.toString())
      if (pageSize) params.append('pageSize', pageSize.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<ProductAnalysisDeepDiveResponse>(`/products/comprehensive${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Comprehensive Inventory Analysis
export function useInventoryComprehensive(fiscalWeek?: number) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['inventory-comprehensive', fiscalWeek, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString())
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<Array<{
        category: string,
        eohTotal: number,
        eohStore: number,
        eohDC: number,
        totalRevenue: number,
        totalUnits: number,
        outOfStock: number,
        wos: number,
        turnRate: number,
        riskLevel: string,
        avgPrice: number
      }>>(`/inventory/comprehensive${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useStorePerformanceDeepDive(
  fiscalWeek?: number,
  searchQuery?: string,
  sortField?: string,
  sortDir?: 'asc' | 'desc',
  page?: number,
  pageSize?: number,
  topFilter?: 'growers' | 'decliners' | null,
) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['store-performance-deep-dive', fiscalWeek, searchQuery, sortField, sortDir, page, pageSize, topFilter, selectedRetailerId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) params.append('week', fiscalWeek.toString())
      if (searchQuery) params.append('q', searchQuery)
      if (sortField) params.append('sort', sortField)
      if (sortDir) params.append('dir', sortDir)
      if (page) params.append('page', page.toString())
      if (pageSize) params.append('pageSize', pageSize.toString())
      if (topFilter) params.append('top', topFilter)
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      const queryString = params.toString()
      return apiClient.get<StorePerformanceDeepDiveResponse>(`/stores/performance-deep-dive${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useInventoryManagement(
  fiscalWeek?: number,
  searchQuery?: string,
  sortField?: string,
  sortDir?: 'asc' | 'desc',
  page?: number,
  pageSize?: number,
  filters?: {
    status?: string[];
    chips?: string[];
    dc_codes?: string[];
    wos_store_band?: string[];
    wos_dc_band?: string[];
  },
  includePerDC?: boolean
) {
  const { selectedRetailerId } = useTenant()
  
  return useQuery({
    queryKey: ['inventory-management', fiscalWeek, searchQuery, sortField, sortDir, page, pageSize, filters, includePerDC, selectedRetailerId],
    queryFn: () => {
      if (!fiscalWeek) {
        throw new Error('fiscalWeek is required for inventory management')
      }
      const params = new URLSearchParams()
      params.append('week', fiscalWeek.toString())
      if (searchQuery) params.append('q', searchQuery)
      if (sortField) params.append('sort', sortField)
      if (sortDir) params.append('dir', sortDir)
      if (page) params.append('page', page.toString())
      if (pageSize) params.append('pageSize', pageSize.toString())
      if (includePerDC) params.append('includePerDC', 'true')
      if (selectedRetailerId) params.append('retailer_id', selectedRetailerId)
      
      if (filters?.status?.length) params.append('status', filters.status.join(','))
      if (filters?.chips?.length) params.append('chips', filters.chips.join(','))
      if (filters?.dc_codes?.length) params.append('dc_codes', filters.dc_codes.join(','))
      if (filters?.wos_store_band?.length) params.append('wos_store_band', filters.wos_store_band.join(','))
      if (filters?.wos_dc_band?.length) params.append('wos_dc_band', filters.wos_dc_band.join(','))
      
      const queryString = params.toString()
      return apiClient.get<InventoryManagementResponse>(`/inventory/management${queryString ? `?${queryString}` : ''}`)
    },
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// MULTI-RETAILER OVERVIEW DATA HOOKS
// ============================================================================

/**
 * Retailer Breakdown - Fetch KPIs for each retailer individually
 * Used by overview dashboard to show per-retailer metrics
 */
export interface RetailerBreakdownItem {
  retailer_id: string;
  retailer_name: string;
  retailer_slug: string;
  revenue: number | null;
  units: number | null;
  skus: number | null;
  stores: number | null;
  onlineRevenue: number | null;
  storeRevenue: number | null;
  hasData: boolean;
}

export function useRetailerBreakdown(fiscalWeek?: number) {
  return useQuery({
    queryKey: ['retailer-breakdown', fiscalWeek],
    queryFn: () => {
      const params = fiscalWeek ? `?fiscal_week=${fiscalWeek}` : '';
      return apiClient.get<RetailerBreakdownItem[]>(`/dashboard/retailer-breakdown${params}`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * KPI Trends - Fetch historical KPI data for sparklines
 */
export interface KPITrendsData {
  weeks: Array<{
    fiscal_week: number;
    revenue: number;
    units: number;
    skus: number;
    stores: number;
    by_retailer: Array<{
      retailer_id: string;
      retailer_name: string;
      revenue: number;
      units: number;
      skus: number;
      stores: number;
    }>;
  }>;
  weeks_requested: number;
  weeks_returned: number;
}

export function useKPITrends(fiscalWeek?: number, weeksBack: number = 8) {
  return useQuery({
    queryKey: ['kpi-trends', fiscalWeek, weeksBack],
    queryFn: () => {
      if (!fiscalWeek) return Promise.resolve(null);
      const params = `?fiscal_week=${fiscalWeek}&weeks_back=${weeksBack}`;
      return apiClient.get<KPITrendsData>(`/dashboard/kpi-trends${params}`);
    },
    enabled: !!fiscalWeek,
    staleTime: 10 * 60 * 1000, // 10 minutes (trend data changes less frequently)
    retry: 1,
  });
}

// ============================================================================
// PIQ 2.0: RETAIL INTELLIGENCE HOOKS
// ============================================================================

export interface RetailMarginData {
  blended: {
    revenue: number;
    cogs: number;
    margin_dollars: number;
    margin_percent: number;
    total_units: number;
    po_count: number;
    sku_count: number;
  };
  retailers: Array<{
    retailer: string;
    revenue: number;
    cogs: number;
    margin_dollars: number;
    margin_percent: number;
    total_units: number;
    po_count: number;
    sku_count: number;
  }>;
}

export function useRetailMargins(dateRange?: { start: string; end: string } | null) {
  return useQuery({
    queryKey: ['retail-margins', dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      if (dateRange?.start) params.append('start_date', dateRange.start);
      if (dateRange?.end) params.append('end_date', dateRange.end);
      const qs = params.toString();
      return apiClient.get<RetailMarginData>(`/retail/margins${qs ? `?${qs}` : ''}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface RetailSKUBreakdownData {
  skus: Array<{
    sku: string;
    base_sku: string;
    product_name: string;
    retailer: string;
    quantity: number;
    revenue: number;
    cogs: number;
    margin_dollars: number;
    margin_percent: number;
  }>;
}

export function useRetailSKUBreakdown(retailer?: string, dateRange?: { start: string; end: string } | null) {
  return useQuery({
    queryKey: ['retail-sku-breakdown', retailer, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      if (retailer) params.append('retailer', retailer);
      if (dateRange?.start) params.append('start_date', dateRange.start);
      if (dateRange?.end) params.append('end_date', dateRange.end);
      const qs = params.toString();
      return apiClient.get<RetailSKUBreakdownData>(`/retail/sku-breakdown${qs ? `?${qs}` : ''}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface RetailPOsData {
  purchase_orders: Array<{
    po_number: string;
    retailer: string;
    po_date: string;
    line_items: number;
    total_units: number;
    total_revenue: number;
    total_cogs: number;
    total_margin: number;
    margin_percent: number;
  }>;
  total: number;
}

export function useRetailPOs(filters?: { retailer?: string; sku?: string; start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['retail-pos', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.retailer) params.append('retailer', filters.retailer);
      if (filters?.sku) params.append('sku', filters.sku);
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);
      const queryString = params.toString();
      return apiClient.get<RetailPOsData>(`/retail/pos${queryString ? `?${queryString}` : ''}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface RetailSellInTrendsData {
  trends: Array<{
    period: string;
    total_units: number;
    total_revenue: number;
    total_cogs: number;
    po_count: number;
    by_retailer: Record<string, { units: number; revenue: number }>;
  }>;
  group_by: string;
}

export function useRetailSellInTrends(groupBy: 'week' | 'month' = 'week', dateRange?: { start: string; end: string } | null) {
  return useQuery({
    queryKey: ['retail-sell-in-trends', groupBy, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('group_by', groupBy);
      if (dateRange?.start) params.append('start_date', dateRange.start);
      if (dateRange?.end) params.append('end_date', dateRange.end);
      return apiClient.get<RetailSellInTrendsData>(`/retail/sell-in-trends?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================
// Briefings / Intelligence Hub
// ============================================================

export interface BriefingListItem {
  id: string;
  briefing_type: 'daily' | 'weekly';
  date_range_start: string;
  date_range_end: string;
  generated_at: string;
  metadata: Record<string, any> | null;
  content_length: number;
}

export interface Briefing extends BriefingListItem {
  markdown_content: string;
}

export interface BriefingsResponse {
  briefings: BriefingListItem[];
  total: number;
  limit: number;
  offset: number;
}

function normalizeMetadata(metadata: unknown): Record<string, any> | null {
  if (metadata === null || metadata === undefined) return null
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, any> : null
    } catch {
      return null
    }
  }
  return typeof metadata === 'object' ? metadata as Record<string, any> : null
}

function normalizeBriefingItem<T extends Partial<BriefingListItem>>(item: T): BriefingListItem {
  return {
    id: String(item.id ?? ''),
    briefing_type: item.briefing_type === 'weekly' ? 'weekly' : 'daily',
    date_range_start: String(item.date_range_start ?? ''),
    date_range_end: String(item.date_range_end ?? ''),
    generated_at: String(item.generated_at ?? ''),
    metadata: normalizeMetadata(item.metadata),
    content_length: Number(item.content_length ?? 0) || 0,
  }
}

function normalizeBriefing(item: Partial<Briefing>): Briefing {
  const base = normalizeBriefingItem(item)
  return {
    ...base,
    markdown_content: String(item.markdown_content ?? ''),
  }
}

export function useBriefings(type?: 'daily' | 'weekly', limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['briefings', type, limit, offset],
    queryFn: () => {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());
      return apiClient.get<BriefingsResponse>(`/briefings/list?${params.toString()}`);
    },
    select: (response): BriefingsResponse => ({
      ...response,
      briefings: (response?.briefings || []).map((item) => normalizeBriefingItem(item)),
    }),
    staleTime: 2 * 60 * 1000,
  });
}

export function useLatestBriefings() {
  return useQuery({
    queryKey: ['briefings-latest'],
    queryFn: () => apiClient.get<{ briefings: Briefing[] }>('/briefings/latest'),
    select: (response) => ({
      briefings: (response?.briefings || []).map((item) => normalizeBriefing(item)),
    }),
    staleTime: 2 * 60 * 1000,
  });
}

export function useBriefing(id: string | null) {
  return useQuery({
    queryKey: ['briefing', id],
    queryFn: () => apiClient.get<Briefing>(`/briefings/detail?id=${encodeURIComponent(id || '')}`),
    enabled: !!id,
    select: (response) => normalizeBriefing(response),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Retail Performance (Sell-In vs Sell-Through)
// ============================================================================

export interface RetailerScorecard {
  retailer: string;
  sellin_units: number;
  sellin_revenue: number;
  sellin_cogs: number;
  sellin_margin_pct: number;
  sellthru_units: number;
  sellthru_revenue: number;
  sellthru_yoy_pct: number;
  sellthru_rate_pct: number;
  eoh_units: number;
  weeks_of_supply: number;
  po_count: number;
  sku_count: number;
  avg_po_units: number;
  earliest_po: string | null;
  latest_po: string | null;
}

export interface SKUPerformance {
  sku: string;
  base_sku: string;
  product_name: string;
  sellin_units: number;
  sellin_revenue: number;
  sellin_margin_pct: number;
  sellthru_units: number;
  sellthru_revenue: number;
  sellthru_rate_pct: number;
  sellthru_yoy_pct: number;
  po_count: number;
  po_frequency_days: number | null;
  risk: 'overstock' | 'stockout' | 'no_replenishment' | null;
}

export function useRetailScorecard(weeks = 13) {
  return useQuery({
    queryKey: ['retail-scorecard', weeks],
    queryFn: () => apiClient.get<{ retailers: RetailerScorecard[]; weeks_back: number }>(
      `/retail/scorecard?weeks=${weeks}`
    ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRetailSKUPerformance(retailer: string | null, weeks = 13) {
  return useQuery({
    queryKey: ['retail-sku-performance', retailer, weeks],
    queryFn: () => apiClient.get<{ retailer: string; skus: SKUPerformance[] }>(
      `/retail/sku-performance?retailer=${retailer}&weeks=${weeks}`
    ),
    enabled: !!retailer,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRetailSellThroughTrends(weeks = 13, retailer?: string) {
  return useQuery({
    queryKey: ['retail-sell-through-trends', weeks, retailer],
    queryFn: () => {
      const params = new URLSearchParams({ weeks: weeks.toString() });
      if (retailer) params.set('retailer', retailer);
      return apiClient.get<any>(`/retail/sell-through-trends?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}
